import { Title, Box, Input, Stack, Switch, Select } from "@skailer-com/uikit";
import { TimeOffCategoryPolicySchema } from "@/pages/settings/time-off/policy/TimeOffCategoryPolicyEdit.tsx";
import { Controller, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ApiTimeOffCountDaysType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";

export interface TimeOffPolicyLimitsProps {
  formHook: UseFormReturn<TimeOffCategoryPolicySchema>;
  periodTrackType?: ApiTimeOffPeriodTrackingTime;
}

export function TimeOffPolicyLimits({ formHook, periodTrackType }: TimeOffPolicyLimitsProps) {
  const { t } = useTranslation();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = formHook;
  const dailyAmountEnabled = watch("daily_amount_enabled");
  const requestTotalEnabled = watch("request_total_enabled");
  const noticePeriodEnabled = watch("notice_period_enabled");
  const attachmentRequiredEnabled = watch("attachment_required_enabled");
  const negativeBalanceAllowed = watch("limits_negative_balance_allowed");

  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;
  const units = isHours ? t("time_off:hours_genitive") : t("time_off:days_genitive");

  return (
    <Stack gap={"sm"}>
      <Title header={t("common:limits")} size={"xs"} />

      <Box variant="border">
        <Controller
          control={control}
          name="attachment_required_enabled"
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={onChange}
              size="sm"
              title={t("time_off:attachment_required_title")}
              description={t("time_off:attachment_required_caption")}
            />
          )}
        />
        {attachmentRequiredEnabled && (
          <Stack direction={"horizontal"} gap={"md"} className={"ml-9"}>
            <Input
              className="w-1/2"
              type="number"
              min={0}
              max={999}
              step="any"
              label={t("time_off:attachment_required_request_total", { units })}
              error={errors.attachment_required_from_request_total?.message}
              {...register("attachment_required_from_request_total")}
            />
            <Input
              className="w-1/2"
              type="number"
              min={0}
              max={999}
              step="any"
              label={t("time_off:attachment_required_cycle_total", { units })}
              error={errors.attachment_required_from_cycle_total?.message}
              {...register("attachment_required_from_cycle_total")}
            />
          </Stack>
        )}
      </Box>

      <Box variant={"border"}>
        <Stack gap={"sm"}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Switch
                checked={value}
                onChange={(newValue) => {
                  onChange(newValue);
                  setValue("negative_balance_in_cycle_allowed", newValue);
                }}
                size={"sm"}
                title={t("time_off:limits_negative_balance_allowance_title")}
                description={t("time_off:limits_negative_balance_allowance_caption", {
                  units: isHours
                    ? t("time_off:hours_genitive").toLowerCase()
                    : t("time_off:days_genitive").toLowerCase(),
                })}
              />
            )}
            control={control}
            name={"limits_negative_balance_allowed"}
          />

          {negativeBalanceAllowed && (
            <Controller
              render={({ field: { value, onChange } }) => (
                <Switch
                  className={"pl-(--gap-lg)"}
                  checked={value}
                  onChange={onChange}
                  size={"sm"}
                  title={t("time_off:negative_balance_in_cycle_allowed_title")}
                  description={t("time_off:negative_balance_in_cycle_allowed_caption")}
                />
              )}
              control={control}
              name={"negative_balance_in_cycle_allowed"}
            />
          )}
        </Stack>
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={(newValue) => {
                onChange(newValue);
              }}
              size={"sm"}
              title={t("time_off:minimum_daily_amount_title")}
              description={t("time_off:minimum_daily_amount_caption", {
                unit: isHours ? t("time_off:hour") : t("time_off:day"),
              })}
            />
          )}
          control={control}
          name={"daily_amount_enabled"}
        />

        {dailyAmountEnabled && (
          <Input
            className={"w-1/2 ml-9"}
            type={"number"}
            label={t("time_off:min_amount")}
            {...register("min_daily_amount")}
          />
        )}
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={(newValue) => {
                onChange(newValue);
              }}
              size={"sm"}
              title={t("time_off:request_amount_title")}
              description={t("time_off:request_amount_caption")}
            />
          )}
          control={control}
          name={"request_total_enabled"}
        />

        {requestTotalEnabled && (
          <Stack direction={"horizontal"} gap={"md"} className={"ml-9"}>
            <Input
              className={"w-1/2 "}
              type={"number"}
              label={t("time_off:min_amount")}
              {...register("min_request_total")}
            />
            <Input
              className={"w-1/2 "}
              type={"number"}
              label={t("time_off:max_amount")}
              {...register("max_request_total")}
            />
          </Stack>
        )}
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={(newValue) => {
                onChange(newValue);
              }}
              size={"sm"}
              title={t("time_off:notice_period_title")}
              description={t("time_off:notice_period_caption")}
            />
          )}
          control={control}
          name={"notice_period_enabled"}
        />

        {noticePeriodEnabled && (
          <Stack direction={"horizontal"} className={"ml-9"} gap={"md"}>
            <Input
              className={"w-1/2"}
              type={"number"}
              label={t("time_off:min_amount")}
              {...register("min_notice_period")}
            />

            <Input
              className={"w-1/2"}
              type={"number"}
              label={t("time_off:max_amount")}
              {...register("max_notice_period")}
            />

            <div className="w-1/2">
              <Controller
                control={control}
                name="notice_period_days_type"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Select
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    options={[
                      {
                        id: ApiTimeOffCountDaysType.calendar_days,
                        title: t("time_off:notice_period_calendar_days"),
                      },
                      {
                        id: ApiTimeOffCountDaysType.working_days,
                        title: t("time_off:notice_period_working_days"),
                      },
                    ]}
                    error={error?.message}
                  />
                )}
              />
            </div>
          </Stack>
        )}
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={onChange}
              size={"sm"}
              title={t("time_off:overlap_requests_allowed_title")}
              description={t("time_off:overlap_requests_allowed_caption")}
            />
          )}
          control={control}
          name={"overlap_requests_allowed"}
        />
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={!value}
              onChange={(newValue) => onChange(!newValue)}
              size={"sm"}
              title={t("time_off:probation_requests_restricted_title")}
              description={t("time_off:probation_requests_restricted_caption")}
            />
          )}
          control={control}
          name={"probation_requests_allowed"}
        />
      </Box>

      <Box variant={"border"}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <Switch
              checked={value}
              onChange={onChange}
              size={"sm"}
              title={t("time_off:past_requests_allowed_title")}
              description={t("time_off:past_requests_allowed_caption")}
            />
          )}
          control={control}
          name={"past_requests_allowed"}
        />
      </Box>
    </Stack>
  );
}
